import { createTransformer } from 'mobx-utils'
import NotificationItemViewModel, {
  NotificationGroupType,
} from './NotificationItemViewModel'
import Workspace from 'iam/models/Workspace'
import NotificationStore from 'notifications/NotificationStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import Notification from 'notifications/models/Notification'
import { computed, action, makeObservable } from 'mobx'
import { Observable } from 'rxjs'
import { task } from 'mobx-task'
import orderBy from 'lodash/orderBy'

/**
 * Notification list view model.
 */
export default class NotificationListViewModel {
  /**
   * Transformer for creating a notification item view model from a notification.
   */
  private createNotificationItemViewModel = createTransformer(
    (item: Notification): NotificationItemViewModel => {
      const vm = new NotificationItemViewModel(
        item,
        this.getTime,
        this.interact,
      )
      vm.activate()
      return vm
    },
    (vm) => vm && vm.deactivate(),
  )

  /**
   * Notifications scroller.
   */
  scroller = new InfiniteScrollProvider({
    limit: 100,
    fetchItems: (params) =>
      this.notificationStore.fetch({
        ...params,
        workspace_id: this.workspace.id || undefined,
      }),
  })

  constructor(
    public workspace: Workspace,
    private notificationStore: NotificationStore,
    private getTime: () => Date,
    private onInteract: (notification: Notification) => unknown,
    loggedIn$: Observable<unknown>,
  ) {
    makeObservable(this)
    // Once we are logged in, fetch the initial data.
    loggedIn$.subscribe(() => this.scroller.fetch())
  }

  /**
   * Groups of notifications.
   */
  @computed
  get notificationGroups(): NotificationGroup[] {
    const collection = this.notificationStore.notifications
    const workspace = this.workspace
    const items =
      workspace !== Workspace.none
        ? collection.filter((n) => n.recipient.workspace === workspace)
        : collection.slice()

    const itemViewModels = orderBy(
      items.map(this.createNotificationItemViewModel),
      'dispatchedAt',
      'desc',
    )
    const today: NotificationGroup = {
      type: NotificationGroupType.Today,
      notifications: [],
    }
    const older: NotificationGroup = {
      type: NotificationGroupType.Older,
      notifications: [],
    }
    itemViewModels.forEach((item) => {
      const group =
        item.notificationGroupType === NotificationGroupType.Today
          ? today
          : older
      group.notifications.push(item)
    })

    return [today, older].filter((g) => g.notifications.length > 0)
  }

  /**
   * Activates the view model
   */
  activate() {
    //
  }

  /**
   * Deactivates the view model
   */
  deactivate() {
    //
  }

  /**
   * Marks the notification as read and notifies the host
   * of this view model of the interaction.
   *
   * @param notification
   */
  @action.bound
  interact(notification: Notification) {
    this.notificationStore.markAsRead(notification)
    this.onInteract(notification)
  }

  /**
   * Mark all notifications as read.
   */
  markAllAsRead = task.resolved(() => {
    return this.notificationStore.markAllAsRead(this.workspace)
  })
}

/**
 * Notification group.
 */
export interface NotificationGroup {
  type: NotificationGroupType
  notifications: NotificationItemViewModel[]
}
